import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LogIn, IdCard, Copy } from "lucide-react";

// Staff card actions: generated Login PIN (Dual-PIN model, shown once) + Staff ID badge lifecycle. Badge tokens are shown once only.
export function StaffQuickLoginActions({ u, badgeFeature, onChanged, onIssued }) {
  const [pinFor, setPinFor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [badges, setBadges] = useState([]);
  const [issued, setIssued] = useState(null);
  const [err, setErr] = useState("");

  const loadBadges = () => badgeFeature && api.get("/staff/badges").then((r) => setBadges(r.data.filter((b) => b.user_id === u.id))).catch(() => {});
  useEffect(() => { loadBadges(); }, [u.id, badgeFeature]); // eslint-disable-line react-hooks/exhaustive-deps
  const active = badges.find((b) => b.status === "active");

  const savePin = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/login-pin`, {});
      toast.success(`New Login PIN issued for ${u.name}`); setPinFor(false); onChanged?.();
      if (data.issued_credentials) onIssued?.(data.issued_credentials);
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const issue = async () => {
    try { const { data } = await api.post("/staff/badges", { user_id: u.id }); setIssued(data); loadBadges(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const act = async (action) => {
    try { const { data } = await api.post(`/staff/badges/${active.id}/${action}`); if (data.badge_code) setIssued(data); toast.success(`Badge ${action === "replace" ? "replaced" : action === "lost" ? "marked lost" : "revoked"}`); loadBadges(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setErr(""); setPinFor(true); }} data-testid={`loginpin-${u.id}`}><LogIn className="w-3.5 h-3.5 mr-1.5" /> {u.has_login_pin ? "Reset Login PIN" : "Set Login PIN"}</Button>
      {badgeFeature && u.role !== "owner" && (active ? (
        <>
          <Badge className="bg-sky-500/15 text-sky-600 text-[10px]" data-testid={`badge-active-${u.id}`}><IdCard className="w-3 h-3 mr-1" /> Badge active</Badge>
          <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => act("replace")} data-testid={`badge-replace-${u.id}`}>Replace</Button>
          <Button size="sm" variant="ghost" className="text-xs h-7 text-amber-600" onClick={() => act("lost")} data-testid={`badge-lost-${u.id}`}>Lost</Button>
          <Button size="sm" variant="ghost" className="text-xs h-7 text-rose-600" onClick={() => act("revoke")} data-testid={`badge-revoke-${u.id}`}>Revoke</Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={issue} data-testid={`badge-issue-${u.id}`}><IdCard className="w-3.5 h-3.5 mr-1.5" /> Issue Staff ID</Button>
      ))}

      <Dialog open={pinFor} onOpenChange={(o) => { if (!o) setPinFor(false); }}>
        <DialogContent className="max-w-xs" data-testid="staff-login-pin-dialog">
          <DialogHeader><DialogTitle className="font-head">Login PIN · {u.name}</DialogTitle>
            <DialogDescription>A new 4-digit Quick Login PIN is generated by the system and shown to you once. {u.has_login_pin ? "The current Login PIN stops working immediately." : ""} It never authorizes protected actions.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {err && <p className="text-sm text-rose-500" data-testid="slp-error">{err}</p>}
            <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={savePin} disabled={busy} data-testid="slp-save">{u.has_login_pin ? "Generate New Login PIN" : "Generate Login PIN"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issued} onOpenChange={(o) => { if (!o) setIssued(null); }}>
        <DialogContent className="max-w-sm" data-testid="badge-issued-dialog">
          <DialogHeader><DialogTitle className="font-head">Staff ID issued · {u.name}</DialogTitle>
            <DialogDescription>Encode this code on the badge (barcode or QR). It identifies the employee only. Their login PIN is still required.</DialogDescription></DialogHeader>
          <div className="rounded-xl border-2 border-sky-500 bg-sky-500/10 p-4 text-center">
            <p className="mono text-xl font-extrabold tracking-wider break-all" data-testid="badge-code">{issued?.badge_code}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => { navigator.clipboard?.writeText(issued.badge_code); toast.success("Copied"); }} data-testid="badge-copy"><Copy className="w-4 h-4 mr-1.5" /> Copy code</Button>
          </div>
          <p className="text-xs text-rose-500">This code cannot be viewed again. If lost, mark the badge lost and issue a replacement.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
